import Head from 'next/head';
import React from 'react';

import { useWebsite } from '../../../../front/src/hooks/useWebsite';
import { html } from '../../../../front/src/utils/dom';
import { truncate } from '../../../../front/src/utils/string';

export type MetaPagePropTypes = {
  title?: string;
  description?: string;
  data?: Record<string, string | number>;
  index: boolean;
  follow: boolean;
  canonicalPath: string | undefined;
  /** `og:type` de la page (ex. `article` en mode média). Absent → non rendu (fallback `MetaWebsite`). */
  ogType?: string;
  /** `og:image` spécifique à la page (ex. image de une d'un article). Absent → fallback `MetaWebsite`. */
  ogImage?: string;
};

const completeTemplate = ({
  template,
  data = {},
}: {
  template?: string;
  data?: Record<string, string | undefined>;
}) => {
  if (!template) return null;
  const completed = template;
  return completed.replace(/{([^{]*)}/g, match => data[match.slice(1, -1)] || '');
};

/**
 * Prédicat `omitBy` pour nettoyer un objet datalayer `graphdebate.meta.*` avant de le spreader
 * sur `MetaPage`. Le resolver renvoie un objet nested Mongoose non-null dont les champs non
 * configurés valent `null`, `undefined` OU `''` (chaîne vide) — un spread brut de ces valeurs
 * écraserait une prop `description`/`title` de fallback posée AVANT le spread. On considère donc
 * « vide » toute valeur nullish ou chaîne vide/espaces (B8_04). Une valeur réellement renseignée
 * (ex. un template `'{pageSubject} - {websiteName}'`) est conservée et garde la priorité.
 */
export const isBlankMeta = (value: unknown): boolean =>
  value == null || (typeof value === 'string' && value.trim() === '');

export const MetaPage: React.FC<MetaPagePropTypes> = ({
  title: titleTemplate,
  description: descriptionTemplate,
  data = {},
  index,
  follow,
  canonicalPath,
  ogType,
  ogImage,
}) => {
  const { name, fullBasepathUrl } = useWebsite();

  const canonicalUrl =
    fullBasepathUrl && canonicalPath ? fullBasepathUrl + canonicalPath : undefined;

  const allData = {
    websiteName: name,
    ...data,
  };

  const title = completeTemplate({
    template: titleTemplate,
    data: allData,
  });

  let description = completeTemplate({
    template: descriptionTemplate,
    data: allData,
  });
  description = description ? truncate(description, 160, true) : description;

  const htmlTitle = html(title ?? '');

  return (
    <Head>
      {title ? (
        <>
          <title key="title">{title}</title>
          <meta
            key="og:title"
            property="og:title"
            content={typeof htmlTitle === 'string' ? htmlTitle : title}
          />
          <meta
            key="twitter:title"
            name="twitter:title"
            content={typeof htmlTitle === 'string' ? htmlTitle : title}
          />
        </>
      ) : null}
      {description ? (
        <>
          <meta key="description" name="description" content={description} />
          <meta key="og:description" property="og:description" content={description} />
        </>
      ) : null}
      <meta
        key="robots"
        name="robots"
        content={[index ? 'index' : 'noindex', follow ? 'follow' : 'nofollow'].join(', ')}
      />

      {canonicalUrl ? (
        <>
          <link key="canonical" rel="canonical" href={canonicalUrl} />
          <meta key="og:url" property="og:url" content={canonicalUrl} />
        </>
      ) : null}

      {/* Override par page (média) — la clé partagée écrase le défaut de MetaWebsite (`website` / logo). */}
      {ogType ? <meta key="og:type" property="og:type" content={ogType} /> : null}
      {ogImage ? <meta key="og:image" property="og:image" content={ogImage} /> : null}
    </Head>
  );
};
